import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Search, Calendar, MapPin, DollarSign, Shield, ChevronRight, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FraudCasesPage({ user, onLogout, refreshUserData }) {
  const { t } = useLanguage();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("");

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/fraud-cases/public`);
      setCases(response.data.cases || []);
    } catch (error) {
      console.error("Error fetching fraud cases:", error);
    } finally {
      setLoading(false);
    }
  };

  const fraudTypes = [
    { value: "", label: "Tous les types" },
    { value: "revente_billets", label: "Revente de billets" },
    { value: "faux_billets", label: "Faux billets" },
    { value: "usurpation_identite", label: "Usurpation d'identité" },
    { value: "arnaque_paiement", label: "Arnaque au paiement" },
    { value: "autre", label: "Autre" },
  ];

  const getRiskLevelBadge = (level) => {
    const colors = {
      1: "bg-green-100 text-green-800",
      2: "bg-yellow-100 text-yellow-800",
      3: "bg-orange-100 text-orange-800",
      4: "bg-red-100 text-red-800",
      5: "bg-red-200 text-red-900",
    };
    const labels = {
      1: "Faible",
      2: "Modéré",
      3: "Moyen",
      4: "Élevé",
      5: "Critique",
    };
    return (
      <Badge className={colors[level] || colors[3]}>
        {labels[level] || "Non défini"}
      </Badge>
    );
  };

  const filteredCases = cases.filter((c) => {
    const matchesSearch =
      c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !selectedType || c.fraud_type === selectedType;
    return matchesSearch && matchesType;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-CA", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return null;
    return new Intl.NumberFormat("fr-CA", {
      style: "currency",
      currency: "CAD",
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-red-600 to-red-800 text-white py-16">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="h-10 w-10" />
                <h1 className="text-3xl sm:text-4xl font-bold">Cas de fraudes</h1>
              </div>
              <p className="text-lg text-red-100 max-w-2xl">
                Découvrez les témoignages de victimes d'arnaques liées à la revente de billets.
                Protégez-vous en apprenant des expériences des autres.
              </p>
              <div className="mt-6 flex flex-wrap gap-4">
                <Link to="/anti-fraude">
                  <Button variant="secondary" className="gap-2">
                    <Shield className="h-4 w-4" />
                    Comment se protéger
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <p className="text-5xl font-bold mb-2">{cases.length}</p>
                <p className="text-red-100">Cas documentés</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="py-8 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Rechercher un cas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              {fraudTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Cases Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
          ) : filteredCases.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                Aucun cas trouvé
              </h3>
              <p className="text-gray-500">
                {searchTerm || selectedType
                  ? "Essayez de modifier vos critères de recherche."
                  : "Aucun cas de fraude n'a encore été documenté."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCases.map((fraudCase) => (
                <Link to={`/fraudes/${fraudCase.slug}`} key={fraudCase.id}>
                  <Card className="h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden group">
                    {fraudCase.cover_image_url && (
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={fraudCase.cover_image_url}
                          alt={fraudCase.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-3 right-3">
                          {fraudCase.risk_level && getRiskLevelBadge(fraudCase.risk_level)}
                        </div>
                      </div>
                    )}
                    <CardContent className={`p-5 ${!fraudCase.cover_image_url ? 'pt-5' : ''}`}>
                      {!fraudCase.cover_image_url && fraudCase.risk_level && (
                        <div className="mb-3">
                          {getRiskLevelBadge(fraudCase.risk_level)}
                        </div>
                      )}
                      <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-red-600 transition-colors">
                        {fraudCase.title}
                      </h3>
                      {fraudCase.excerpt && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {fraudCase.excerpt}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                        {fraudCase.publish_date && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(fraudCase.publish_date)}
                          </div>
                        )}
                        {fraudCase.location && (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {fraudCase.location}
                          </div>
                        )}
                        {fraudCase.amount_lost && (
                          <div className="flex items-center gap-1 text-red-600 font-medium">
                            <DollarSign className="h-4 w-4" />
                            {formatAmount(fraudCase.amount_lost)}
                          </div>
                        )}
                      </div>
                      <div className="mt-4 flex items-center text-red-600 text-sm font-medium">
                        Lire l'histoire
                        <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-brand-600 to-brand-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="h-16 w-16 mx-auto mb-6 opacity-80" />
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Protégez-vous avec Middleman
          </h2>
          <p className="text-brand-100 max-w-2xl mx-auto mb-8">
            Utilisez notre plateforme sécurisée pour vos transactions de billets.
            Nous vérifions chaque transaction pour vous protéger des arnaques.
          </p>
          <Link to="/auth">
            <Button size="lg" variant="secondary" className="gap-2">
              Commencer maintenant
              <ChevronRight className="h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
